import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, FileText, Copy, Upload, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeOptions = [
  { value: "post", label: "Post" },
  { value: "reel", label: "Reel" },
  { value: "both", label: "Post & Reel" },
];

const defaultVariables = ["{firma}", "{branche}", "{saison}", "{thema}", "{zielgruppe}", "{leistung}", "{ort}"];

const emptyForm = { company_id: "", name: "", type: "post", template_text: "", variables: [], is_active: true };

export default function PromptTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { t: tr } = useLanguage();

  const load = async () => {
    try {
      const [tpl, c] = await Promise.all([
        axios.get(`${API}/prompt-templates`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }),
      ]);
      setTemplates(tpl.data);
      setCompanies(c.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/prompt-templates/${editing}`, form, { withCredentials: true });
        toast.success("Template aktualisiert");
      } else {
        await axios.post(`${API}/prompt-templates`, form, { withCredentials: true });
        toast.success("Template erstellt");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (e) { toast.error("Fehler"); }
  };

  const handleEdit = (t) => {
    setEditing(t.id);
    setForm({ ...emptyForm, ...t });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Template loeschen?")) return;
    try {
      await axios.delete(`${API}/prompt-templates/${id}`, { withCredentials: true });
      toast.success("Geloescht");
      load();
    } catch { toast.error("Fehler"); }
  };

  const handleDuplicate = async (t) => {
    try {
      await axios.post(`${API}/prompt-templates`, {
        ...t, name: `${t.name} (Kopie)`, id: undefined
      }, { withCredentials: true });
      toast.success("Template dupliziert");
      load();
    } catch { toast.error("Fehler"); }
  };

  const handlePublish = async (t) => {
    try {
      await axios.post(
        `${API}/prompt-templates/${t.id}/publish`,
        { tags: [], description: "" },
        { withCredentials: true }
      );
      toast.success("Template im Marketplace veroeffentlicht");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Veroeffentlichen fehlgeschlagen");
    }
  };

  const toggleVariable = (v) => {
    setForm((f) => {
      const vars = f.variables || [];
      return { ...f, variables: vars.includes(v) ? vars.filter((x) => x !== v) : [...vars, v] };
    });
  };

  const insertVariable = (v) => {
    setForm((f) => ({ ...f, template_text: (f.template_text || "") + " " + v }));
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "Unbekannt";

  return (
    <div data-testid="templates-page" className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight">{tr("templates.title")}</h1>
          <p className="text-[var(--studio-text-muted)] mt-1">{tr("templates.subtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-template-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
              <Plus className="w-4 h-4 mr-2" /> {tr("templates.add_btn")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Template bearbeiten" : "Neues Template"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[var(--studio-text-muted)]">Firma *</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm((f) => ({ ...f, company_id: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Firma waehlen" /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[var(--studio-text-muted)]">Typ</Label>
                  <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {typeOptions.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Template-Name *</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1"
                  placeholder="z.B. Motivations-Post StrongFit" />
              </div>
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Template-Text</Label>
                <Textarea
                  value={form.template_text}
                  onChange={(e) => setForm((f) => ({ ...f, template_text: e.target.value }))}
                  className="mt-1 font-mono text-sm"
                  rows={6}
                  placeholder="Erstelle einen motivierenden Post fuer {firma} zum Thema {thema}. Nutze die Tonalitaet: energisch und positiv. Fokus auf {leistung}."
                />
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="text-xs text-[var(--studio-text-muted)] mr-1">Variablen einfuegen:</span>
                  {defaultVariables.map((v) => (
                    <button key={v} onClick={() => insertVariable(v)}
                      className="text-xs px-2 py-0.5 rounded bg-[var(--studio-bg)] border border-[var(--studio-border)] hover:border-[#2563EB] transition-colors">
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
                <Label className="text-sm">Aktiv (wird bei der Generierung beruecksichtigt)</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => { setOpen(false); setEditing(null); setForm(emptyForm); }}>Abbrechen</Button>
              <Button onClick={handleSave} data-testid="save-template-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                disabled={!form.name || !form.company_id}>
                {editing ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {templates.length === 0 ? (
        <Card className="border-[var(--studio-border)] shadow-none">
          <CardContent className="py-16 text-center">
            <FileText className="w-12 h-12 mx-auto text-[var(--studio-text-muted)] opacity-40 mb-4" />
            <p className="text-[var(--studio-text-muted)]">{tr("templates.empty")}</p>
            <p className="text-sm text-[var(--studio-text-muted)] mt-1">{tr("templates.empty_hint")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {templates.map((t) => (
            <Card key={t.id} className="border-[var(--studio-border)] shadow-none" data-testid={`template-card-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-sm">{t.name}</h3>
                      <Badge variant="outline" className="text-[10px] capitalize">{t.type}</Badge>
                      <Badge className={`text-[10px] border-0 ${t.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                        {t.is_active ? "Aktiv" : "Inaktiv"}
                      </Badge>
                    </div>
                    <p className="text-xs text-[var(--studio-text-muted)]">{companyName(t.company_id)}</p>
                    {t.template_text && (
                      <p className="text-xs mt-2 p-2 bg-[var(--studio-bg)] rounded font-mono line-clamp-2">{t.template_text}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {t.is_shared ? (
                      <Button variant="ghost" size="icon" disabled title="Im Marketplace" data-testid={`shared-badge-${t.id}`}>
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="icon" onClick={() => handlePublish(t)} title="Im Marketplace veroeffentlichen" data-testid={`publish-template-${t.id}`}>
                        <Upload className="w-4 h-4 text-[#2563EB]" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => handleDuplicate(t)} title="Duplizieren" data-testid={`dup-template-${t.id}`}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(t)} data-testid={`edit-template-${t.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(t.id)} data-testid={`delete-template-${t.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
